/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';

// Component imports
import { Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import { GraphContext } from '../../FlowGraph';
import { ArrowDownIcon } from '../../../../components/Icons/ArrowDownIcon';

// Misc imports
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const { Text } = Typography;
const baseClassName = 'sticky-note-node';

const StickyNoteNode = ({ id, type, data, dragging, selected }) => {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);
  const arrowRef = useRef(null);
  const contentRef = useRef(null);
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { currentBotLanguage } = useBotLanguage();
  const [shouldDisplayExpandArrow, setShouldDisplayExpandArrow] = useState(false);
  const { graphDirection } = useContext(GraphContext);

  useEffect(() => {
    const el = contentRef.current;

    if (!el) {
      return;
    }

    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    const maxHeight = lineHeight * 4;
    // If content is taller than 4 lines, clamp is needed
    if (el.scrollHeight > maxHeight + 15) {
      setShouldDisplayExpandArrow(true);
    } else {
      setShouldDisplayExpandArrow(false);
    }
  }, [data.label[currentBotLanguage]]);

  const NodeContents = useMemo(() => {
    return (
      <div ref={wrapperRef} className={`${baseClassName}__wrapper`} >
        {
          data.label[currentBotLanguage] ? (
            <Text ref={contentRef} className={`${baseClassName}__note-message`} type="secondary">
              <ReactMarkdown
                skipHtml
                linkTarget="_blank"
              >
                {data.label[currentBotLanguage]}
              </ReactMarkdown>
            </Text>
          ) : (
            <Text>
              {t('Write some content')}
            </Text>
          )
        }

        {
          data.label[currentBotLanguage] && shouldDisplayExpandArrow ? (
            <div ref={arrowRef} className={`${baseClassName}__expand-arrow w-100 text-center`} onClick={e => {
              wrapperRef.current?.classList.toggle('expanded');
              arrowRef.current?.classList.toggle('rotated');
            }}>
              <ArrowDownIcon />
            </div>
          ) : null
        }
      </div>
    );
  }, [data, data.label[currentBotLanguage], shouldDisplayExpandArrow, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
StickyNoteNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string
};

export default memo(StickyNoteNode);
